import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { API_URL } from '../utils/Functions';
import TopBar from '../components/TopBar';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const UserLoginPage = () => {
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Forgot password states
  const [step, setStep] = useState('login'); // 'login' | 'forgot_login' | 'forgot_otp'
  const [forgotLoginId, setForgotLoginId] = useState('');
  const [forgotOtp, setForgotOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [showNewPass, setShowNewPass] = useState(false);

  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ login_id: loginId, password }),
      });
      const res = await response.json();
      if (!response.ok) throw res;

      const { access_token, user } = res.data;

      if (user.role === 'admin') {
        setLoading(false);
        setError('Access Denied: This login page is for standard users only. Please use the Admin Portal.');
        return;
      }

      localStorage.setItem('auth_token', access_token);
      localStorage.setItem('auth_user', JSON.stringify(user));
      navigate('/user/dashboard');
    } catch (err) {
      setLoading(false);
      if (err.status === 'unverified') {
        setError('Your account is not verified. Please check your email for the verification OTP.');
      } else if (err.status === 'inactive') {
        setError('Your account has been deactivated. Please contact support.');
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('Unable to connect to the server. Please try again.');
      }
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(''); setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ email_or_phone: forgotLoginId }),
      });
      const res = await response.json();
      if (!response.ok) throw res;
      setSuccess('A password reset OTP has been sent to your email/phone.');
      setStep('forgot_otp');
    } catch (err) {
      setError(err.message || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(''); setLoading(true);
    if (newPassword !== newPasswordConfirm) {
      setError('Passwords do not match.');
      setLoading(false);
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          email_or_phone: forgotLoginId,
          otp: forgotOtp,
          password: newPassword,
          password_confirmation: newPasswordConfirm
        }),
      });
      const res = await response.json();
      if (!response.ok) throw res;
      setSuccess('Your password has been successfully reset. You can now login.');
      setStep('login');
      setPassword('');
      setLoginId(forgotLoginId);
    } catch (err) {
      setError(err.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <TopBar />
      <Navbar />
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');

        .ul-wrapper {
          min-height: 100vh;
          display: flex;
          font-family: 'Inter', sans-serif;
          background: #f5f7fa;
          position: relative;
          overflow: hidden;
        }

        .ul-bg-shape {
          position: absolute; border-radius: 50%;
          pointer-events: none; filter: blur(70px); opacity: 0.45;
          animation: ulShapeFloat 10s ease-in-out infinite alternate;
        }
        .ul-bg-shape-1 { width: 520px; height: 520px; background: radial-gradient(circle, #a7f3d0, transparent 70%); top: -160px; right: -80px; }
        .ul-bg-shape-2 { width: 380px; height: 380px; background: radial-gradient(circle, #c7d2fe, transparent 70%); bottom: -80px; left: -80px; animation-delay: -5s; }
        .ul-bg-shape-3 { width: 250px; height: 250px; background: radial-gradient(circle, #bbf7d0, transparent 70%); top: 40%; left: 28%; animation-delay: -2s; }

        @keyframes ulShapeFloat {
          0%   { transform: translate(0, 0) scale(1); }
          100% { transform: translate(15px, -20px) scale(1.04); }
        }

        .ul-bg-grid {
          position: absolute; inset: 0;
          background-image: radial-gradient(rgba(99,102,241,0.07) 1px, transparent 1px);
          background-size: 36px 36px; pointer-events: none;
        }

        /* ── Left Panel ── */
        .ul-left-panel {
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          padding: 48px 60px;
          position: relative;
          z-index: 1;
          overflow: hidden;
        }

        .ul-left-panel::before {
          content: '';
          position: absolute; inset: 0;
          background: url('https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?auto=format&fit=crop&w=1200&q=80') center/cover no-repeat;
          z-index: -2;
        }

        .ul-left-panel::after {
          content: '';
          position: absolute; inset: 0;
          background: linear-gradient(160deg, rgba(6,24,48,0.9) 0%, rgba(6,24,48,0.7) 50%, rgba(207,160,71,0.6) 100%);
          z-index: -1;
        }

        .ul-left-content {
          max-width: 480px; width: 100%;
          position: relative; z-index: 2;
          text-align: left;
          display: flex; flex-direction: column;
          height: 100%; justify-content: space-between;
        }

        .ul-brand { display: flex; align-items: center; gap: 12px; margin-bottom: 64px; }

        .ul-brand-icon {
          width: 44px; height: 44px;
          background: rgba(255,255,255,0.15);
          border: 1.5px solid rgba(255,255,255,0.35);
          border-radius: 12px;
          display: flex; align-items: center; justify-content: center;
          font-size: 18px; color: white;
        }

        .ul-brand-text { font-size: 22px; font-weight: 800; color: #ffffff; letter-spacing: -0.3px; }

        .ul-hero-tag {
          display: inline-flex; align-items: center; gap: 8px;
          background: rgba(255,255,255,0.12);
          border: 1px solid rgba(255,255,255,0.25);
          color: rgba(255,255,255,0.95);
          font-size: 11px; font-weight: 700;
          letter-spacing: 1.4px; text-transform: uppercase;
          padding: 6px 16px; border-radius: 50px; margin-bottom: 28px;
        }
        .ul-hero-tag-dot {
          width: 6px; height: 6px; background: #CFA047; border-radius: 50%;
          box-shadow: 0 0 8px rgba(207,160,71,0.8);
        }

        .ul-hero-title {
          font-size: 52px; font-weight: 800; color: #ffffff;
          line-height: 1.12; letter-spacing: -2.5px; margin: 0 0 22px 0;
        }
        .ul-hero-title span { color: #CFA047; }

        .ul-hero-desc {
          font-size: 16px; color: rgba(255,255,255,0.78);
          line-height: 1.75; margin-bottom: 52px; max-width: 400px;
        }

        .ul-features { display: flex; flex-direction: column; gap: 14px; }
        .ul-feature-item { display: flex; align-items: center; gap: 14px; }
        .ul-feature-icon {
          width: 36px; height: 36px; border-radius: 10px;
          background: rgba(255,255,255,0.12);
          border: 1px solid rgba(255,255,255,0.22);
          display: flex; align-items: center; justify-content: center;
          color: #CFA047; font-size: 14px; flex-shrink: 0;
        }
        .ul-feature-text { font-size: 14px; font-weight: 500; color: rgba(255,255,255,0.88); }

        /* ── Right Panel ── */
        .ul-right-panel {
          width: 520px; min-width: 520px;
          background: #ffffff;
          display: flex; align-items: center; justify-content: center;
          padding: 56px 48px;
          position: relative; z-index: 1;
          border: 3px solid #061830;
        }

        .ul-watermark {
          position: absolute; color: #061830; opacity: 0.045;
          pointer-events: none; user-select: none; line-height: 1;
        }

        @media (max-width: 900px) {
          .ul-left-panel { display: none; }
          .ul-right-panel { width: 100%; min-width: 100%; }
        }

        .ul-card { width: 100%; max-width: 400px; }

        .ul-card-header { margin-bottom: 40px; text-align: center; }
        .ul-card-title {
          font-size: 34px; font-weight: 800; color: #0f172a;
          margin: 0 0 4px; letter-spacing: -1.2px; line-height: 1.1;
        }
        .ul-title-bar {
          width: 48px; height: 4px;
          background: linear-gradient(90deg, #061830, #CFA047);
          border-radius: 4px; margin: 14px auto 16px;
        }
        .ul-card-subtitle { font-size: 14.5px; color: #64748b; line-height: 1.6; }

        /* Form */
        .ul-form-group { margin-bottom: 22px; }
        .ul-form-label {
          display: block; margin-bottom: 8px;
          font-weight: 700; font-size: 12.5px;
          color: #1e293b; letter-spacing: 0.4px; text-transform: uppercase;
        }
        .ul-input-wrap { position: relative; }
        .ul-input-icon {
          position: absolute; left: 16px; top: 50%; transform: translateY(-50%);
          color: #94a3b8; font-size: 14px; pointer-events: none; transition: color 0.2s;
        }
        .ul-input-wrap:focus-within .ul-input-icon { color: #CFA047; }
        .ul-form-input {
          width: 100%; padding: 15px 44px 15px 46px;
          background: #f8fafc; border: 1.5px solid #e2e8f0;
          border-radius: 12px; font-size: 15px; color: #0f172a;
          font-family: 'Inter', sans-serif;
          transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;
          box-sizing: border-box;
        }
        .ul-form-input::placeholder { color: #cbd5e1; }
        .ul-form-input:focus {
          outline: none; border-color: #CFA047; background: #fff;
          box-shadow: 0 0 0 4px rgba(207,160,71,0.15), 0 2px 8px rgba(0,0,0,0.06);
        }

        .ul-pass-toggle {
          position: absolute; right: 14px; top: 50%; transform: translateY(-50%);
          color: #94a3b8; background: none; border: none; cursor: pointer;
          padding: 0; font-size: 14px; transition: color 0.18s;
        }
        .ul-pass-toggle:hover { color: #CFA047; }

        .ul-form-row {
          display: flex; justify-content: space-between; align-items: center; margin-bottom: 30px;
        }
        .ul-remember { display: flex; align-items: center; gap: 8px; cursor: pointer; }
        .ul-remember input[type="checkbox"] { width: 16px; height: 16px; accent-color: #CFA047; cursor: pointer; }
        .ul-remember-label { font-size: 13px; color: #64748b; cursor: pointer; }
        .ul-forgot {
          font-size: 13px; color: #061830; text-decoration: none;
          font-weight: 700; transition: color 0.18s;
        }
        .ul-forgot:hover { color: #CFA047; text-decoration: underline; }

        .ul-submit-btn {
          width: 100%; padding: 16px;
          background: #061830;
          border: none; border-radius: 50px;
          color: white; font-size: 15.5px; font-weight: 700;
          cursor: pointer; transition: all 0.25s;
          font-family: 'Inter', sans-serif;
          display: flex; align-items: center; justify-content: center; gap: 10px;
          box-shadow: 0 10px 28px rgba(6,24,48,0.32);
          letter-spacing: 0.3px;
        }
        .ul-submit-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 16px 36px rgba(6,24,48,0.45); }
        .ul-submit-btn:active:not(:disabled) { transform: scale(0.98); }
        .ul-submit-btn:disabled { opacity: 0.75; cursor: not-allowed; }

        .ul-spinner {
          width: 17px; height: 17px;
          border: 2px solid rgba(255,255,255,0.35); border-top-color: white;
          border-radius: 50%; animation: ulSpin 0.7s linear infinite;
        }
        @keyframes ulSpin { to { transform: rotate(360deg); } }

        .ul-error-banner {
          display: flex; align-items: flex-start; gap: 10px;
          background: #fff5f5; border: 1.5px solid #fca5a5;
          border-radius: 10px; padding: 12px 16px;
          margin-bottom: 20px; font-size: 13.5px; color: #b91c1c;
          line-height: 1.5; animation: ulBannerIn 0.25s ease;
        }
        .ul-error-banner i { margin-top: 2px; flex-shrink: 0; }
        @keyframes ulBannerIn { from { opacity:0; transform:translateY(-6px); } to { opacity:1; transform:translateY(0); } }

        .ul-divider { display: flex; align-items: center; gap: 12px; margin: 28px 0 20px; }
        .ul-divider-line { flex: 1; height: 1px; background: #f1f5f9; }
        .ul-divider-text { font-size: 11.5px; color: #94a3b8; white-space: nowrap; font-weight: 600; letter-spacing: 0.5px; }

        .ul-footer-note { text-align: center; font-size: 13px; color: #64748b; line-height: 1.6; }
        .ul-footer-note a { color: #061830; font-weight: 700; text-decoration: none; }
        .ul-footer-note a:hover { color: #CFA047; text-decoration: underline; }
      `}</style>

      <div className="ul-wrapper">
        <div className="ul-bg-grid"></div>
        <div className="ul-bg-shape ul-bg-shape-1"></div>
        <div className="ul-bg-shape ul-bg-shape-2"></div>
        <div className="ul-bg-shape ul-bg-shape-3"></div>

        {/* Left Hero Panel */}
        <div className="ul-left-panel">
          <div className="ul-left-content">

            <div className="ul-brand">
              <img src="/images/logo.png" alt="Logo" style={{ width: '120px', height: '120px', objectFit: 'contain' }} />
            </div>

            <div>
              <div className="ul-hero-tag">
                <span className="ul-hero-tag-dot"></span>
                Your Trusted Tax Partner
              </div>
              <h1 className="ul-hero-title">
                Simplify Your<br />
                Tax Filing<br />
                with <span>Confidence</span>
              </h1>
              <p className="ul-hero-desc">
                Access all your tax services, documents, and filings
                from one secure and easy-to-use portal.
              </p>
            </div>

            <div className="ul-features">
              {[
                { icon: 'fa-file-invoice-dollar', text: 'Track your filings and tax documents in real time' },
                { icon: 'fa-shield-halved', text: 'Bank-grade security to protect your financial data' },
                { icon: 'fa-headset', text: 'Expert CPA support available whenever you need it' },
              ].map((f, i) => (
                <div key={i} className="ul-feature-item">
                  <div className="ul-feature-icon">
                    <i className={`fa-solid ${f.icon}`}></i>
                  </div>
                  <span className="ul-feature-text">{f.text}</span>
                </div>
              ))}
            </div>

          </div>
        </div>

        {/* Right Form Panel */}
        <div className="ul-right-panel">
          <i className="fa-solid fa-building-columns ul-watermark" style={{ fontSize: '120px', top: '-20px', right: '-20px', transform: 'rotate(15deg)' }}></i>
          <i className="fa-solid fa-file-invoice-dollar ul-watermark" style={{ fontSize: '90px', bottom: '30px', left: '-15px', transform: 'rotate(-20deg)' }}></i>
          <i className="fa-solid fa-shield-halved ul-watermark" style={{ fontSize: '70px', top: '40%', right: '20px', transform: 'rotate(10deg)' }}></i>
          <i className="fa-solid fa-chart-line ul-watermark" style={{ fontSize: '60px', top: '20px', left: '20px', transform: 'rotate(-10deg)' }}></i>
          <i className="fa-solid fa-scale-balanced ul-watermark" style={{ fontSize: '55px', bottom: '80px', right: '40px', transform: 'rotate(20deg)' }}></i>

          <div className="ul-card">
            <div className="ul-card-header">
              <h2 className="ul-card-title">
                {step === 'login' ? 'Welcome back' : step === 'forgot_login' ? 'Forgot Password' : 'Reset Password'}
              </h2>
              <div className="ul-title-bar"></div>
              <p className="ul-card-subtitle">
                {step === 'login'
                  ? 'Sign in to your account to continue'
                  : step === 'forgot_login'
                  ? 'Enter your email or phone to receive a reset code'
                  : 'Enter the OTP and your new password'}
              </p>
            </div>

            {error && (
              <div className="ul-error-banner" role="alert">
                <i className="fa-solid fa-circle-exclamation"></i>
                <span>{error}</span>
              </div>
            )}
            {success && (
              <div className="ul-error-banner" role="alert" style={{ background: '#f0fdf4', borderColor: '#86efac', color: '#15803d' }}>
                <i className="fa-solid fa-circle-check"></i>
                <span>{success}</span>
              </div>
            )}

            {step === 'login' && (
              <form onSubmit={handleLogin}>
                <div className="ul-form-group">
                  <label className="ul-form-label" htmlFor="ul-login-id">Email or Phone</label>
                  <div className="ul-input-wrap">
                    <i className="fa-solid fa-user ul-input-icon"></i>
                    <input
                      id="ul-login-id"
                      type="text"
                      className="ul-form-input"
                      placeholder="email@example.com or phone"
                      value={loginId}
                      onChange={(e) => setLoginId(e.target.value)}
                      required
                      autoComplete="username"
                    />
                  </div>
                </div>

                <div className="ul-form-group">
                  <label className="ul-form-label" htmlFor="ul-password">Password</label>
                  <div className="ul-input-wrap">
                    <i className="fa-solid fa-lock ul-input-icon"></i>
                    <input
                      id="ul-password"
                      type={showPass ? 'text' : 'password'}
                      className="ul-form-input"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete="current-password"
                    />
                    <button type="button" className="ul-pass-toggle" onClick={() => setShowPass(!showPass)} tabIndex={-1}>
                      <i className={`fa-solid ${showPass ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                    </button>
                  </div>
                </div>

                <div className="ul-form-row">
                  <label className="ul-remember">
                    <input type="checkbox" id="ul-remember" />
                    <span className="ul-remember-label">Remember me</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => { setStep('forgot_login'); setError(''); setSuccess(''); }}
                    className="ul-forgot"
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                  >
                    Forgot password?
                  </button>
                </div>

                <button type="submit" id="ul-login-submit" className="ul-submit-btn" disabled={loading}>
                  {loading ? (
                    <><span className="ul-spinner"></span>Signing in…</>
                  ) : (
                    <><i className="fa-solid fa-arrow-right-to-bracket"></i>Sign In</>
                  )}
                </button>
              </form>
            )}

            {step === 'forgot_login' && (
              <form onSubmit={handleForgotSubmit}>
                <div className="ul-form-group">
                  <label className="ul-form-label">Email or Phone</label>
                  <div className="ul-input-wrap">
                    <i className="fa-solid fa-user ul-input-icon"></i>
                    <input
                      type="text"
                      className="ul-form-input"
                      placeholder="email@example.com or phone"
                      value={forgotLoginId}
                      onChange={(e) => setForgotLoginId(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <button type="submit" className="ul-submit-btn" disabled={loading}>
                  {loading ? (
                    <><span className="ul-spinner"></span>Sending…</>
                  ) : (
                    <><i className="fa-solid fa-paper-plane"></i>Send Reset OTP</>
                  )}
                </button>

                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                  <button type="button" onClick={() => { setStep('login'); setError(''); setSuccess(''); }} className="ul-forgot" style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
                    ← Back to Login
                  </button>
                </div>
              </form>
            )}

            {step === 'forgot_otp' && (
              <form onSubmit={handleResetPasswordSubmit}>
                <div className="ul-form-group">
                  <label className="ul-form-label">One-Time Password (OTP)</label>
                  <div className="ul-input-wrap">
                    <i className="fa-solid fa-key ul-input-icon"></i>
                    <input
                      type="text"
                      maxLength="6"
                      className="ul-form-input"
                      placeholder="Enter 6-digit OTP"
                      value={forgotOtp}
                      onChange={(e) => setForgotOtp(e.target.value.replace(/\D/g, ''))}
                      required
                      style={{ letterSpacing: '4px' }}
                    />
                  </div>
                </div>

                <div className="ul-form-group">
                  <label className="ul-form-label">New Password</label>
                  <div className="ul-input-wrap">
                    <i className="fa-solid fa-lock ul-input-icon"></i>
                    <input
                      type={showNewPass ? 'text' : 'password'}
                      className="ul-form-input"
                      placeholder="Min. 8 characters"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                    />
                    <button type="button" className="ul-pass-toggle" onClick={() => setShowNewPass(!showNewPass)} tabIndex={-1}>
                      <i className={`fa-solid ${showNewPass ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                    </button>
                  </div>
                </div>

                <div className="ul-form-group">
                  <label className="ul-form-label">Confirm New Password</label>
                  <div className="ul-input-wrap">
                    <i className="fa-solid fa-lock-open ul-input-icon"></i>
                    <input
                      type={showNewPass ? 'text' : 'password'}
                      className="ul-form-input"
                      placeholder="Re-enter password"
                      value={newPasswordConfirm}
                      onChange={(e) => setNewPasswordConfirm(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <button type="submit" className="ul-submit-btn" disabled={loading || forgotOtp.length !== 6}>
                  {loading ? (
                    <><span className="ul-spinner"></span>Resetting…</>
                  ) : (
                    <><i className="fa-solid fa-check-circle"></i>Reset Password</>
                  )}
                </button>

                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                  <button type="button" onClick={() => { setStep('login'); setError(''); setSuccess(''); }} className="ul-forgot" style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
                    Cancel &amp; Back to Login
                  </button>
                </div>
              </form>
            )}

            <div className="ul-divider">
              <div className="ul-divider-line"></div>
              <span className="ul-divider-text">Don't have an account?</span>
              <div className="ul-divider-line"></div>
            </div>

            <p className="ul-footer-note">
              <Link to="/register">Create a free account →</Link>
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default UserLoginPage;
